import { useState, useEffect, useCallback } from 'react';
import {
  AgxCaption,
  AgxInputText,
  AgxColumn,
  AgxRow,
  Images,
  AgxBodyText,
  ValueType,
} from '@urbanx/agx-ui-components';
import { parseFloatReturnEmptyIfZero } from 'utils/parseFloatReturnEmptyIfZero';
import { VendorChoice } from './types';
import './VendorChoiceCommission.scss';
import { parseStringToNumber } from 'utils/numbers';
import {
  AgxDropdown,
  AgxTextInput,
  Breakpoints,
  useWindowSize,
} from '@urbanx/agx.ui-components2';

const options = [
  { label: ValueType.Percentage, value: ValueType.Percentage },
  { label: ValueType.Dollar, value: ValueType.Dollar },
];

interface VendorChoiceCommissionProps {
  id: string;
  onValueChanged: (value: { id: string; value: VendorChoice }) => void;
  defaultValue?: VendorChoice;
  showCommissionRangeDisclaimer?: boolean;
  validate?: boolean;
  required?: boolean;
}

const VendorChoiceCommission = ({
  id,
  onValueChanged,
  defaultValue,
  showCommissionRangeDisclaimer,
  validate: validatePage,
  required,
}: VendorChoiceCommissionProps) => {
  const screenSize = useWindowSize();

  const isDesktop = screenSize === Breakpoints.Desktop;
  const isTablet =
    screenSize === Breakpoints.Tablet_Landscape ||
    screenSize === Breakpoints.Tablet_Portrait;

  const [minCommission, setMinCommission] = useState(
    defaultValue?.minCommission
  );
  const [maxCommission, setMaxCommission] = useState(
    defaultValue?.maxCommission
  );
  const [error, setError] = useState<string | undefined>(undefined);
  const [isDollarSelected, setIsDollarSelected] = useState(
    defaultValue?.valueType === ValueType.Dollar
  );
  const [selectedOption, setSelectedOption] = useState(
    options.find(o => o.value === defaultValue?.valueType) ?? options[0]
  );

  const validate = useCallback(() => {
    if (Number(minCommission) === 0 && Number(maxCommission) > 0) {
      setError('Min Commission must not be empty');
    } else if (
      Number(minCommission) > 0 &&
      Number(maxCommission) > 0 &&
      Number(maxCommission) <= Number(minCommission)
    ) {
      setError(`Max commission must be greater than Min commission`);
    } else if (Number(maxCommission) === 0 && Number(minCommission)) {
      setError(undefined);
    } else {
      setError(undefined);
    }
  }, [minCommission, maxCommission, setError]);

  const onOptionChange = useCallback(
    (value: string) => {
      if (value === selectedOption.value) return;
      setSelectedOption(options.find(o => o.value === value) ?? options[0]);
      setIsDollarSelected(value === ValueType.Dollar);
      // reset min/max values on option change
      setMinCommission(undefined);
      setMaxCommission(undefined);
      validate();
    },
    [
      selectedOption.value,
      setSelectedOption,
      setIsDollarSelected,
      setMinCommission,
      setMaxCommission,
      validate,
    ]
  );

  useEffect(() => {
    validate();
    onValueChanged({
      id,
      value: {
        minCommission: !minCommission ? 0.0 : minCommission,
        maxCommission: !maxCommission ? 0.0 : maxCommission,
        valueType: selectedOption.value,
      },
    });
  }, [minCommission, maxCommission, selectedOption]);

  return (
    <>
      <AgxColumn fill largeGap>
        <AgxColumn fill smallGap>
          {!isDesktop && !isTablet && (
            <AgxDropdown
              options={options}
              id={`select-${id}`}
              selected={selectedOption.value}
              onChange={value => onOptionChange(value)}
              label="Percentage/Dollar"
              required
              hideOptionalText
            />
          )}
          <AgxRow largeGap fill>
            {(isDesktop || isTablet) && (
              <AgxDropdown
                options={options}
                id={`select-${id}`}
                selected={selectedOption.value}
                onChange={value => onOptionChange(value)}
                label="Percentage/Dollar"
                required
                hideOptionalText
              />
            )}
            <AgxRow mediumGap fill extraClasses="rangeTextStyle">
              <AgxTextInput
                key={`min-${id}-${isDollarSelected ? 'dollar' : 'percent'}`}
                type={isDollarSelected ? 'currency' : 'percent'}
                id="minCommission"
                hideOptionalText
                label="Min Commission"
                onInputValueChange={({ value }) => {
                  const parsedNumber = parseFloatReturnEmptyIfZero(value.replaceAll(',', ''));
                  const validNumber =
                    parsedNumber === '' ? 0 : (parsedNumber as number);

                  setMinCommission(validNumber);
                }}
                defaultValue={minCommission?.toString() ?? ''}
                errorText={error}
                hideErrorDescription
                stretch
                noDecimalPoints={isDollarSelected}
              />
              <AgxInputText>to</AgxInputText>
              <AgxTextInput
                key={`max-${id}-${isDollarSelected ? 'dollar' : 'percent'}`}
                type={isDollarSelected ? 'currency' : 'percent'}
                id="maxCommission"
                hideOptionalText
                label="Max Commission"
                onInputValueChange={({ value }) => {
                  const parsedNumber = parseFloatReturnEmptyIfZero(value.replaceAll(',', ''));
                  const validNumber =
                    parsedNumber === '' ? 0 : (parsedNumber as number);

                  setMaxCommission(validNumber);
                }}
                defaultValue={maxCommission?.toString() ?? ''}
                errorText={error}
                hideErrorDescription
                stretch
                noDecimalPoints={isDollarSelected}
              />
            </AgxRow>
          </AgxRow>
          {error && (
            <AgxRow fill mediumGap>
              <Images.AlertCircle />
              <AgxBodyText
                small
                extraClasses="error"
                dataTestId={`error-${id}`}
              >
                {error}
              </AgxBodyText>
            </AgxRow>
          )}
        </AgxColumn>
        {showCommissionRangeDisclaimer ? (
          <AgxCaption>
            Between {isDollarSelected ? '$' : ''}
            {Number(minCommission) || 0}
            {!isDollarSelected ? '%' : ''} and {isDollarSelected ? '$' : ''}
            {Number(maxCommission) || 0}
            {!isDollarSelected ? '%' : ''} of the selling price to be determined
            by the Principal
          </AgxCaption>
        ) : (
          <AgxCaption>
            A fee range of between {isDollarSelected ? '$' : ''}
            {Number(minCommission) || 0}
            {!isDollarSelected ? '%' : ''} and {isDollarSelected ? '$' : ''}
            {Number(maxCommission) || 0}
            {!isDollarSelected ? '%' : ''} inclusive of GST of the actual sale
            price to be determined by the client once the sale contract has
            become unconditional. It is to be based on your level of
            satisfaction with the level of service experience and result we
            achieve.
          </AgxCaption>
        )}
      </AgxColumn>
    </>
  );
};

export default VendorChoiceCommission;
